import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Database, Warehouse, Truck, Users, ClipboardList,
  ChevronDown, ChevronRight, UserCog, MapPin, ArrowRightLeft, CalendarDays,
  Tag, BarChart2, FileBarChart, Settings, CalendarClock, Map, ClipboardCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8583e645-db93-4117-b2c4-4d2d1bbcb032/artifacts/5imn8h2x_cc.png";

/* Roles that have restricted nav visibility */
const RESTRICTED_NAV_ROLES = new Set(["supervisor", "relationship_manager", "warehouse_manager"]);

const NAV = [
  { label: "Dashboard",    icon: LayoutDashboard,  path: "/dashboard",    testId: "nav-dashboard",    roles: null },
  { label: "Users",        icon: UserCog,           path: "/users",        testId: "nav-users",        roles: [] },
  {
    label: "Master Data",
    icon: Database,
    testId: "nav-master-data",
    roles: [],
    children: [
      { label: "Warehouses",   icon: Warehouse, path: "/master-data/warehouses",   testId: "nav-warehouses"   },
      { label: "Vehicles",     icon: Truck,     path: "/master-data/vehicles",     testId: "nav-vehicles"     },
      { label: "Cities",       icon: MapPin,    path: "/master-data/cities",       testId: "nav-cities"       },
      { label: "Regions",      icon: Map,       path: "/master-data/regions",      testId: "nav-regions"      },
      { label: "Delivery Associates", icon: Users, path: "/master-data/delivery-associates", testId: "nav-delivery-associates" },
      { label: "Lead Sources", icon: Tag,       path: "/master-data/lead-sources", testId: "nav-lead-sources" },
    ],
  },
  {
    label: "Calendars",
    icon: CalendarDays,
    testId: "nav-calendars",
    roles: null,
    children: [
      { label: "All Events",            icon: CalendarDays,  path: "/calendar",          testId: "nav-calendar-all",      roles: ["supervisor", "relationship_manager"] },
      { label: "Follow-Up",             icon: CalendarClock, path: "/calendar/followup", testId: "nav-calendar-followup", roles: ["supervisor", "relationship_manager"] },
      { label: "Pick-ups & Delivery",   icon: Truck,         path: "/calendar/movement", testId: "nav-calendar-movement", roles: ["supervisor", "relationship_manager", "warehouse_manager"] },
    ],
  },
  { label: "Leads", icon: Users, testId: "nav-leads", roles: ["supervisor", "relationship_manager"], children: [
    { label: "All", icon: Users, path: "/leads/all", testId: "nav-leads-all", roles: ["supervisor", "relationship_manager"] },
    { label: "Active", icon: Users, path: "/leads/active", testId: "nav-leads-active", roles: ["supervisor", "relationship_manager"] },
    { label: "Confirmed", icon: Users, path: "/leads/confirmed", testId: "nav-leads-confirmed", roles: ["supervisor", "relationship_manager"] },
    { label: "Closed", icon: Users, path: "/leads/closed", testId: "nav-leads-closed", roles: ["supervisor", "relationship_manager"] },
  ] },
  { label: "Confirmed Leads", icon: ClipboardList, testId: "nav-bookings", roles: ["supervisor", "relationship_manager", "warehouse_manager"], children: [
    { label: "Active", icon: ClipboardList, path: "/confirmed-leads/active", testId: "nav-bookings-active", roles: ["supervisor", "relationship_manager", "warehouse_manager"] },
    { label: "Completed", icon: ClipboardList, path: "/confirmed-leads/completed", testId: "nav-bookings-completed", roles: ["supervisor", "relationship_manager", "warehouse_manager"] },
  ] },
  { label: "Consignments", icon: ArrowRightLeft, testId: "nav-transfers", roles: ["supervisor", "relationship_manager", "warehouse_manager"], children: [
    { label: "Active", icon: ArrowRightLeft, path: "/consignments/active", testId: "nav-consignments-active", roles: ["supervisor", "relationship_manager", "warehouse_manager"] },
    { label: "Completed", icon: ArrowRightLeft, path: "/consignments/completed", testId: "nav-consignments-completed", roles: ["supervisor", "relationship_manager", "warehouse_manager"] },
  ] },
  { label: "Trip Sheets", icon: Truck, testId: "nav-consignments", roles: ["supervisor", "relationship_manager", "warehouse_manager"], children: [
    { label: "Active", icon: Truck, path: "/trips/active", testId: "nav-trips-active", roles: ["supervisor", "relationship_manager", "warehouse_manager"] },
    { label: "Completed", icon: Truck, path: "/trips/completed", testId: "nav-trips-completed", roles: ["supervisor", "relationship_manager", "warehouse_manager"] },
  ] },
  {
    label: "Reporting",
    icon: BarChart2,
    testId: "nav-reporting",
    roles: ["supervisor"],
    children: [
      { label: "Warehouse Report", icon: FileBarChart, path: "/reporting/warehouse-report", testId: "nav-warehouse-report", roles: ["supervisor"] },
      { label: "Transfer Report",  icon: FileBarChart, path: "/reporting/transfer-report",  testId: "nav-transfer-report", roles: ["supervisor"] },
      { label: "Vehicle Report",   icon: FileBarChart, path: "/reporting/vehicle-report",   testId: "nav-vehicle-report", roles: ["supervisor"] },
    ],
  },
  { label: "Daily Summary", icon: ClipboardCheck, path: "/daily-summary", testId: "nav-daily-summary", roles: ["supervisor"] },
  { label: "Settings", icon: Settings, path: "/settings", testId: "nav-settings", roles: [] },
];

export default function Sidebar({ collapsed }) {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { user }   = useAuth();
  const role       = user?.role || "";
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = ["Master Data"];
    // Auto-expand group if current path matches any child
    const path = window.location.pathname;
    NAV.forEach(item => {
      if (item.children?.some(c => path === c.path || path.startsWith(c.path + "/"))) {
        initial.push(item.label);
      }
    });
    return initial;
  });

  /* Filter nav items the current role can see:
     - Non-restricted roles (admin, sales_executive, etc.) → see everything
     - Restricted roles → only items with roles:null or roles including their role */
  const isRestricted = RESTRICTED_NAV_ROLES.has(role);
  const visibleNav = NAV
    .filter(item => {
      if (!isRestricted) return true;          // admin / unrestricted → all items
      if (item.roles === null) return true;    // explicitly visible to all
      return item.roles.includes(role);        // restricted: must be in allow-list
    })
    .map(item => {
      if (!item.children || !isRestricted) return item;
      return {
        ...item,
        children: item.children.filter(child =>
          child.roles === null || child.roles?.includes(role)
        ),
      };
    })
    .filter(item => !item.children || item.children.length > 0);

  const isActive = (itemOrPath) => {
    const item = typeof itemOrPath === "string" ? { path: itemOrPath } : itemOrPath;
    if (item.prefix) {
      return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
    }
    // exact match, but treat /calendar as prefix only when it IS the base route
    return location.pathname === item.path;
  };
  const isGroupActive = (children) =>
    children?.some((c) => location.pathname.startsWith(c.path));

  const toggleGroup = (label) =>
    setOpenGroups((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-[#1B2A6B] z-40 flex flex-col transition-all duration-300 ease-in-out ${
        collapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Logo */}
      <div
        className={`h-16 flex items-center border-b border-white/10 shrink-0 ${
          collapsed ? "justify-center px-2" : "px-5"
        }`}
      >
        {collapsed ? (
          <div className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center">
            <span className="text-[#1B2A6B] text-xs font-black">CC</span>
          </div>
        ) : (
          <img src={LOGO_URL} alt="GoSmart Dashboard" className="h-10 object-contain" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-0.5 px-2">
        {visibleNav.map((item) => {
          /* ── Group with children ── */
          if (item.children) {
            const open = openGroups.includes(item.label);
            const groupActive = isGroupActive(item.children);
            return (
              <div key={item.label}>
                <button
                  onClick={() => !collapsed && toggleGroup(item.label)}
                  title={collapsed ? item.label : undefined}
                  data-testid={item.testId}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-150 border-l-4 ${
                    groupActive
                      ? "border-[#FFD700] bg-white/10 text-white"
                      : "border-transparent text-white/70 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <item.icon size={18} className="shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      {open ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </>
                  )}
                </button>

                {/* Sub-items */}
                {!collapsed && open && (
                  <div className="ml-4 border-l border-white/10 pl-3 mt-0.5 space-y-0.5">
                    {item.children.map((child) => (
                      <button
                        key={child.path}
                        onClick={() => navigate(child.path)}
                        data-testid={child.testId}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-all duration-150 border-l-4 ${
                          isActive(child.path)
                            ? "border-[#FFD700] bg-white/10 text-white"
                            : "border-transparent text-white/60 hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        <child.icon size={14} className="shrink-0" />
                        {child.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          /* ── Single item ── */
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              title={collapsed ? item.label : undefined}
              data-testid={item.testId}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-150 border-l-4 ${
                isActive(item)
                  ? "border-[#FFD700] bg-white/10 text-white"
                  : "border-transparent text-white/70 hover:bg-white/5 hover:text-white"
              }`}
            >
              <item.icon size={18} className="shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Footer brand */}
      {!collapsed && (
        <div className="px-5 py-4 border-t border-white/10 shrink-0">
          <p className="text-[11px] text-white/30 font-medium">GoSmart Dashboard</p>
          <p className="text-[10px] text-white/20 mt-0.5">Lead Management Portal v1.0</p>
        </div>
      )}
    </aside>
  );
}
